import { BarDatum, ResponsiveBar } from "@nivo/bar";
import { PropsWithChildren, useEffect, useState } from "react";

const useCssVar = (varName: string, fallback: string) => {
  const [value, setValue] = useState(fallback);
  useEffect(() => {
    const root = document.documentElement;
    const cssValue = getComputedStyle(root).getPropertyValue(varName).trim();
    if (cssValue) setValue(cssValue);
  }, [varName]);
  return value;
};

type Props = PropsWithChildren<{
  chartData: BarDatum[];
  keyMap: Record<string, string>;
  onDetailsClick?: (resource: BarDatum) => void;
}>;

export const BarChart = ({ chartData, keyMap, onDetailsClick }: Props) => {
  const axisColor = useCssVar("--text-dark", "#CCC");
  const ticksColor = useCssVar("--border-default-primary", "#CCC");
  const labelColor = useCssVar("--text-light", "#CCC");
  const tooltipColor = useCssVar("--text-light", "#CCC");
  const tooltipBackgroundColor = useCssVar("--text-dark", "#CCC");

  const transformedData = chartData.map((item) => {
    const newItem: BarDatum = {
      id: item.id,
      client: item.client,
    };
    Object.entries(keyMap).forEach(([internalKey, label]) => {
      if (item[internalKey] !== undefined) {
        newItem[label] = item[internalKey];
      }
    });
    return newItem;
  });

  const keys = Object.values(keyMap);

  return (
    <ResponsiveBar
      data={transformedData}
      animate={true}
      onMouseEnter={(_datum, event) => {
        if (onDetailsClick) {
          event.currentTarget.style.cursor = "pointer";
        }
      }}
      keys={keys}
      indexBy="client"
      onClick={(bar) => {
        if (onDetailsClick && bar.data) {
          onDetailsClick(bar.data);
        }
      }}
      margin={{ top: 10, right: 30, bottom: 50, left: 60 }}
      padding={0.3}
      valueScale={{ type: "linear" }}
      indexScale={{ type: "band", round: true }}
      colors={{ scheme: "nivo" }}
      defs={[
        {
          id: "lines",
          type: "patternLines",
          background: "inherit",
          color: "#eed312",
          rotation: -45,
          lineWidth: 6,
          spacing: 10,
        },
      ]}
      fill={[
        {
          match: {
            id: "Shared login",
          },
          id: "lines",
        },
      ]}
      borderColor={{
        from: "color",
        modifiers: [["darker", 1.6]],
      }}
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: "Client name",
        legendPosition: "middle",
        legendOffset: 30,
        truncateTickAt: 0,
      }}
      axisLeft={{
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,
        legend: "Notifications",
        legendPosition: "middle",
        legendOffset: -40,
        truncateTickAt: 0,
      }}
      theme={{
        axis: {
          ticks: { text: { fill: axisColor } },
          legend: { text: { fill: axisColor } },
        },
        grid: {
          line: {
            stroke: ticksColor,
          },
        },
        labels: {
          text: { fill: labelColor },
        },
        tooltip: {
          container: {
            color: tooltipColor,
            background: tooltipBackgroundColor,
            fontSize: "12px",
          },
        },
      }}
      labelSkipWidth={12}
      labelSkipHeight={12}
      labelTextColor={labelColor} // <-- And this line
      role="Notifications"
      ariaLabel="Number of notifications per client"
      barAriaLabel={(e) =>
        e.id + ": " + e.formattedValue + " in client: " + e.indexValue
      }
    />
  );
};
